import React from 'react'
import { AlertCircle, Bot, CheckCircle2, Coins, MonitorCheck, Scale, Timer } from 'lucide-react'

function number(value, suffix = '') {
  return `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(Number(value) || 0)}${suffix}`
}

function money(value, currency = 'KRW') {
  const safeCurrency = currency || 'KRW'
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: safeCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: safeCurrency === 'KRW' ? 2 : 4,
  }).format(Number(value) || 0)
}

function costAmounts(costs, divisor = 1) {
  if (costs.length === 0 || divisor <= 0) return '-'
  return costs.map((item) => money((Number(item.amount) || 0) / divisor, item.currency)).join(' · ')
}

function AlertRow({ alert }) {
  const tone = alert.severity === 'HIGH'
    ? 'border-rose-200 bg-rose-50 text-rose-800'
    : alert.severity === 'MEDIUM'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-emerald-200 bg-emerald-50 text-emerald-800'
  const Icon = alert.severity === 'OK' ? CheckCircle2 : AlertCircle
  return (
    <div className={`flex items-start gap-3 border px-4 py-3 ${tone}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        <div className="text-sm font-semibold">{alert.title}</div>
        <div className="mt-1 text-xs leading-5 opacity-90">{alert.action}</div>
      </div>
    </div>
  )
}

export default function CounselPracticeQualitySnapshot({ data }) {
  if (!data) return null
  const level = data.serviceLevel || {}
  const devices = data.devices || {}
  const calibration = data.calibration || {}
  const models = Array.isArray(data.providerModels) ? data.providerModels : []
  const clients = Array.isArray(devices.clients) ? devices.clients : []
  const costs = Array.isArray(data.costs) ? data.costs : []
  const costEventCount = costs.reduce((total, item) => total + (Number(item.eventCount) || 0), 0)
  const costTokenCount = costs.reduce((total, item) => total + (Number(item.tokenCount) || 0), 0)
  const unpricedCount = costs.reduce((total, item) => total + (Number(item.unpricedCount) || 0), 0)
  const completedCount = Number(level.completedCount) || 0

  return (
    <div className="mt-6 space-y-6" data-testid="counsel-quality-snapshot">
      <section aria-labelledby="quality-alert-title">
        <h3 id="quality-alert-title" className="text-base font-semibold text-slate-950">확인할 항목</h3>
        <div className="mt-3 grid gap-2 xl:grid-cols-2">
          {(data.alerts || []).map((alert, index) => <AlertRow key={`${alert.title}-${index}`} alert={alert} />)}
        </div>
      </section>

      <section className="border-t border-slate-200 pt-5" aria-labelledby="service-quality-title">
        <h3 id="service-quality-title" className="text-base font-semibold text-slate-950">서비스 품질</h3>
        <div className="mt-4 grid overflow-hidden border border-slate-200 sm:grid-cols-2 xl:grid-cols-5">
          {[
            [Bot, 'AI 응답 성공률', number(level.aiSuccessRate, '%'), `${number(level.sampleCount, '건')} 기준`],
            [AlertCircle, '대체 응답 비율', number(level.fallbackRate, '%'), 'AI 대신 제공한 기본 답변'],
            [Timer, '95% 응답 시간', number((level.p95LatencyMs || 0) / 1000, '초'), `${number(level.latencySampleCount, '건')} 측정`],
            [AlertCircle, '결과 분석 실패', number(level.evaluationFailureRate, '%'), '완료 후 점수 산출 실패'],
            [Scale, '점수 기준', calibration.statusLabel || '자료 확인 중', number(calibration.independentReviewCount, '건의 동료 평가')],
          ].map(([Icon, label, value, detail]) => (
            <div key={label} className="min-w-0 px-4 py-4">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><Icon className="h-4 w-4 text-sky-700" aria-hidden="true" />{label}</div>
              <div className="mt-2 break-words text-lg font-semibold text-slate-950">{value}</div>
              <div className="mt-1 text-xs text-slate-500">{detail}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 pt-5" aria-labelledby="counsel-cost-title">
        <h3 id="counsel-cost-title" className="text-base font-semibold text-slate-950">AI 사용 비용</h3>
        <div className="mt-4 grid overflow-hidden border border-slate-200 sm:grid-cols-2 xl:grid-cols-4">
          {[
            [Coins, '예상 비용', costAmounts(costs), `${number(data.periodDays, '일')} 합계`],
            [Bot, 'AI 응답', number(costEventCount, '건'), `${number(costTokenCount, '토큰')} 사용`],
            [Scale, '완료 연습당', costAmounts(costs, completedCount), `${number(completedCount, '건')} 기준`],
            [AlertCircle, '단가 미설정', number(unpricedCount, '건'), unpricedCount > 0 ? '비용 단가 확인 필요' : '모두 계산됨'],
          ].map(([Icon, label, value, detail]) => (
            <div key={label} className="min-w-0 px-4 py-4">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><Icon className="h-4 w-4 text-teal-700" aria-hidden="true" />{label}</div>
              <div className="mt-2 break-words text-lg font-semibold text-slate-950">{value}</div>
              <div className="mt-1 text-xs text-slate-500">{detail}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 pt-5" aria-labelledby="provider-model-title">
        <h3 id="provider-model-title" className="text-base font-semibold text-slate-950">AI 모델별 현황</h3>
        <div className="mt-4 overflow-x-auto border-y border-slate-200">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr><th className="px-4 py-3 font-medium">공급자</th><th className="px-4 py-3 font-medium">모델</th><th className="px-4 py-3 text-right font-medium">응답</th><th className="px-4 py-3 text-right font-medium">성공률</th><th className="px-4 py-3 text-right font-medium">대체 응답</th><th className="px-4 py-3 text-right font-medium">평균 시간</th><th className="px-4 py-3 text-right font-medium">토큰</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {models.map((item) => (
                <tr key={`${item.provider}-${item.model}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{item.provider}</td><td className="px-4 py-3 text-slate-700">{item.model}</td><td className="px-4 py-3 text-right">{number(item.responseCount, '건')}</td><td className="px-4 py-3 text-right font-semibold">{number(item.successRate, '%')}</td><td className="px-4 py-3 text-right">{number(item.fallbackRate, '%')}</td><td className="px-4 py-3 text-right">{number((item.averageLatencyMs || 0) / 1000, '초')}</td><td className="px-4 py-3 text-right">{number(item.totalTokens)}</td>
                </tr>
              ))}
              {models.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">아직 모델별 응답 기록이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="border-t border-slate-200 pt-5" aria-labelledby="device-quality-title">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 id="device-quality-title" className="flex items-center gap-2 text-base font-semibold text-slate-950"><MonitorCheck className="h-5 w-5 text-teal-700" aria-hidden="true" />마이크 사용 환경</h3>
          <span className="text-sm text-slate-600">점검 통과 {number(devices.readyRate, '%')} · {number(devices.checkCount, '건')}</span>
        </div>
        <div className="mt-4 overflow-x-auto border-y border-slate-200">
          <table className="w-full min-w-[680px] text-left text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-4 py-3 font-medium">환경</th><th className="px-4 py-3 font-medium">브라우저</th><th className="px-4 py-3 text-right font-medium">점검</th><th className="px-4 py-3 text-right font-medium">통과율</th><th className="px-4 py-3 text-right font-medium">권한 차단</th><th className="px-4 py-3 text-right font-medium">장치 없음</th><th className="px-4 py-3 text-right font-medium">소리 없음</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {clients.map((item, index) => <tr key={`${item.platformLabel}-${item.browserLabel}-${item.runtimeLabel}-${index}`}><td className="px-4 py-3 font-medium text-slate-800">{item.platformLabel} · {item.runtimeLabel}</td><td className="px-4 py-3 text-slate-700">{item.browserLabel}</td><td className="px-4 py-3 text-right">{number(item.checkCount, '건')}</td><td className="px-4 py-3 text-right font-semibold">{number(item.readyRate, '%')}</td><td className="px-4 py-3 text-right">{number(item.permissionDeniedCount, '건')}</td><td className="px-4 py-3 text-right">{number(item.noDeviceCount, '건')}</td><td className="px-4 py-3 text-right">{number(item.noSignalCount, '건')}</td></tr>)}
              {clients.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">아직 마이크 점검 기록이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
